import React, { useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { avatarColor, initialOf, STATUS, PRIO, TYPE, typeTitle } from "../../design/ui";
import { normalizeTask, normalizeProject, statusKey, prioKey, isOverdue, typeKey } from "../../design/domain";
import Empty from "../../components/design/Empty";
import { PRIO_KIND, SearchField, StatusChip, TASK_STATUS_KIND } from "../../components/mui";
import { useListPage } from "../../components/design/ListPager";
import { buildRdRows } from "./rdSummary";
import { MdChevronLeft, MdChevronRight, MdClose, MdOutlinedFlag } from "react-icons/md";

/**
 * 時程日曆 — 版面基準為 DESIGN 稿 L308-L325（月格 + 顯示專案膠囊）。
 *
 * 相對設計稿的擴充（設計稿只有月格，實務上排不出「今天要做什麼」）：
 *   月 / 週 / 議程三種檢視、今天鈕、國定假日、事件類型與指派人篩選、
 *   跨日的工項與里程碑在月檢視畫成一條連續長條（同 Google 日曆）、
 *   點事件或點日期開「當日詳情」彈出視窗。
 * 工具列、篩選、視窗用 MUI 元件；月格／週欄是自製視覺化，用 Box sx 畫。
 */

/* 設計稿 CALPROJ 的專案色（前三色照抄），專案數超過時依序循環 */
const PALETTE = ["#0e7c66", "#2563eb", "#7c5cff", "#dc6803", "#d94b8f", "#0891b2", "#d92d20", "#8a94a3"];

const WEEKDAYS = ["日", "一", "二", "三", "四", "五", "六"];

/* 檢視模式 */
const VIEWS = [
  { k: "month", label: "月" },
  { k: "week", label: "週" },
  { k: "agenda", label: "議程" },
];

/**
 * 檢視粒度。
 *
 * 預設「專案」：這一頁多數時候是拿來回答「現在有哪些專案在跑、哪些做完了」，
 * 而一個月的工項起訖動輒數百則，整張月曆會被 `PM-202607-004-34 開始` 這種
 * 代號洗版 —— 看的人既認不出那是哪個專案，也看不出專案整體走到哪。
 * 專案模式一個專案只畫一條，標的是專案名稱與推估進度；要看細節點開長條即可。
 */
const SCOPES = [
  { k: "project", label: "專案" },
  { k: "task", label: "工項" },
  /* RD：一天一人一列「姓名 N 件」。工項＝每日日報，這一層要看的是「今天誰交了、誰沒交」，
     所以空白的日子才是重點，不是把有資料的日子畫出來就好（見 rdSummary.js）。 */
  { k: "rd", label: "RD" },
];

/* 事件類型：篩選膠囊、議程徽章、圖例共用同一組色 */
const KINDS = [
  { k: "start", label: "工項開始", c: "#2563eb" },
  { k: "due", label: "工項截止", c: "#dc6803" },
  { k: "phase", label: "里程碑", c: "#7c5cff" },
  { k: "meeting", label: "會議", c: "#0891b2" },
  { k: "holiday", label: "假日", c: "#d92d20" },
];
const KIND_MAP = KINDS.reduce((a, x) => {
  a[x.k] = x;
  return a;
}, {});
/* 同一天內的排序：假日（當日背景資訊）→ 會議 → 里程碑 → 工項開始 → 工項截止 */
const KIND_ORDER = { holiday: 0, meeting: 1, phase: 2, start: 3, due: 4 };

/* 跨日長條中段那幾天在當日視窗裡的「性質」標示。
   刻意不寫「進行中」：那是 design/ui 的 STATUS.progress 標籤（全站藍色狀態膠囊），
   而同一列右邊本來就會畫出該工項真正的狀態膠囊（可能是「待處理」），
   兩個互相矛盾的標籤並排只會讓人以為看錯。 */
const SPAN_MID_NATURE = "進行期間";

const AGENDA_DAYS = 30;
const ALL_ASSIGNEE = "__all__";
const NO_ASSIGNEE = "__none__";
/* 月格最多塞 4 個事件，超過收成「+N 更多」（同設計稿）。
   demo 資料一個月有 900+ 事件，不截斷的話單一格就能把整張月曆撐到數千 px 高。 */
const MAX_CHIPS = 4;
/* 週檢視的欄位比月格高很多，但同樣不能無上限，超過一樣收成「+N 更多」開當日視窗 */
const MAX_CHIPS_WEEK = 8;
/* 當日詳情視窗與議程檢視的每頁筆數 */
const PAGE_SIZE = 10;

/* 月檢視跨日長條的版面常數。
   長條覆蓋層是絕對定位的，所以它離格子上緣的距離要跟格子內容自己算出來的一致：
   格子 padding-top 6px + 日期列 19px + 3px 間距 = 28px；每層佔 17px（長條 14px + 3px 間距）。 */
const SPAN_TOP = 28;
const SPAN_ROW = 17;
const SPAN_H = 14;
/* 一週最多疊 3 層長條。再多的併進當日的「+N 更多」——
   demo 資料一週有上百條跨日工項，不設上限單一週就能疊到兩千 px 高。 */
const MAX_LANES = 3;
/* 專案模式一個專案只有一條，數量級本來就小很多（幾個到十幾個），
   還套 3 層會把大半專案擠進「+N 更多」，正好蓋掉這個模式要回答的問題 */
const MAX_LANES_PROJECT = 8;

/* 共用的空陣列常數：當日沒有事件時傳這個，參考位址才不會每次 render 都變 */
const EMPTY_EVENTS = [];

const pad2 = (n) => String(n).padStart(2, "0");

/**
 * 清單 API 可能回陣列，也可能回 { data_list } 分頁包裝，兩種都吸收（同 Gantt.js 慣例）。
 * 回 {}、null、{ data_list: null } 或任何非陣列都一律降級成 []，後續 .map/.forEach 才不會炸。
 */
function toArray(raw) {
  if (Array.isArray(raw)) return raw;
  const arr = _.get(raw, "data_list");
  return Array.isArray(arr) ? arr : [];
}

/** Date → `2026-07-27`（本頁一律用這種字串當日期鍵，字串比大小即等於日期比大小） */
function ymdKey(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/**
 * 後端回的是帶時區的 DateTimeOffset，直接 new Date() 會因時差跨日，
 * 因此字串是 ISO / 斜線格式時優先取字面上的年月日。
 */
function ymdOf(v) {
  if (!v) return "";
  const s = String(v);
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const slash = s.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})/);
  if (slash) return `${slash[1]}-${pad2(slash[2])}-${pad2(slash[3])}`;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "" : ymdKey(d);
}

/** 取字面上的時分；00:00 視為「只有日期沒有時間」不顯示，避免整排假的 00:00 */
function timeOf(v) {
  const m = String(v || "").match(/[T ](\d{2}):(\d{2})/);
  if (!m) return "";
  if (m[1] === "00" && m[2] === "00") return "";
  return `${m[1]}:${m[2]}`;
}

/** `2026-07-27` → Date（本地零時，不經 UTC 解析以免整個差一天） */
function ymdToDate(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ""));
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function addDays(d, n) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

/* ===== 跨日長條的純計算（scratchpad/cal 有對應的 node 測試，改這段請一併跑） ===== */

/**
 * 把原始起訖正規化成「真正要畫的區間」。
 * 只有一端，或結束早於開始（後端偶爾會有這種資料），一律當單日處理，不畫長條。
 * 回 null 表示兩端都沒有，根本畫不出東西。
 */
function spanRange(startYmd, endYmd) {
  const s = String(startYmd || "");
  const e = String(endYmd || "");
  if (!s && !e) return null;
  if (!s) return { s: e, e };
  if (!e || e < s) return { s, e: s };
  return { s, e };
}

/**
 * 算一個事件在某一週要從第幾格畫到第幾格。
 *
 * cells 是該週 7 格的日期鍵，月初／月末的空格傳 null —— 跨月時只會取到落在當月的格子，
 * 所以回傳的 c0/c1 天生就被裁在當月範圍內，不必另外處理月份邊界。
 * roundStart / roundEnd 表示這一段的頭尾是不是「真正的起訖端」；不是的話那一側不收圓角，
 * 看起來就像續接到上／下一週（或續出當月之外）。
 *
 * ymd 是「這一段畫在畫面上的第一天」，也就是點這條長條要開哪一天的視窗。
 * 不能拿整條的真起點來開：起點在上個月時（例如 6/28 → 7/3，7 月只畫得到尾段），
 * 那個日期不在目前檢視的區間內，視窗會被「換月後關掉視窗」的 effect 立刻收掉，
 * 使用者看到的就是遮罩閃一下、這條長條點不開。取 cells[c0] 一定是畫面上存在的格子。
 *
 * 回 null 表示這一週完全碰不到這個事件。
 */
function weekSegment(startYmd, endYmd, cells) {
  const r = spanRange(startYmd, endYmd);
  if (!r) return null;
  let c0 = -1;
  let c1 = -1;
  for (let i = 0; i < cells.length; i += 1) {
    const ymd = cells[i];
    if (ymd && ymd >= r.s && ymd <= r.e) {
      if (c0 < 0) c0 = i;
      c1 = i;
    }
  }
  if (c0 < 0) return null;
  return { c0, c1, ymd: cells[c0], roundStart: cells[c0] === r.s, roundEnd: cells[c1] === r.e };
}

/**
 * 這一天是不是落在長條的「中段」（起訖兩天不算）。
 * 當日視窗靠它補出「進行期間」那幾列；起訖兩天本來就有「開始」「截止」列，補了會重複。
 */
function isSpanMiddle(span, ymd) {
  if (!ymd || !span) return false;
  return span.s < ymd && ymd < span.e;
}

/**
 * 同一週的長條分層：逐條找「第一個放得下的層」，放不下就開新的一層。
 * 呼叫端要先依 c0 由小到大排序，否則貪心法會空出用不到的層。
 * 不改動傳進來的物件，回傳補上 lane 的新陣列。
 */
function packLanes(segments) {
  const ends = [];
  return segments.map((seg) => {
    let lane = ends.findIndex((end) => end < seg.c0);
    if (lane < 0) {
      lane = ends.length;
      ends.push(-1);
    }
    ends[lane] = seg.c1;
    return { ...seg, lane };
  });
}

/* ===== 跨日長條的純計算結束 ===== */

/**
 * 原始日期字串 → `2026/7/27`。
 * 不用 domain.fmtDate 是因為它走 new Date()，後端的 DateTimeOffset 會因時差跨日；
 * 本頁一律以 ymdOf 取字面上的年月日，顯示才會跟月曆格對得起來。
 */
function ymdLabel(v) {
  const ymd = ymdOf(v);
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-");
  return `${y}/${Number(m)}/${Number(d)}`;
}

/**
 * 工項說明在後端是富文本（存的是 HTML），清單只放兩行預覽，
 * 直接印會看到一堆 <p> 標籤，所以去標籤取純文字。
 */
function plainText(v) {
  return String(v || "")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<\/(p|div|li|h[1-6])>/gi, " ")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * 模糊搜尋用的比對字串：把該事件所有「看得到的文字」串成一條小寫字串。
 * 預先算好存在事件上，搜尋時就不必每次 keystroke 重組數千筆字串。
 */
function searchBlob(o) {
  return [
    o.code,
    o.name,
    o.title,
    o.projectName,
    o.phaseName,
    o.assignee,
    o.assigneeKey,
    o.nature,
    o.desc,
    o.time,
    o.ymd,
    ymdLabel(o.startRaw),
    ymdLabel(o.endRaw),
    _.get(STATUS, [o.status, "label"], ""),
    _.get(PRIO, [o.prio, "label"], ""),
    _.get(TYPE, [o.issueType, "label"], ""),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

/** 空白分隔的關鍵字視為 AND；空字串直接回原陣列（不複製，讓 useMemo 的比較能命中） */
function matchQuery(list, q) {
  const toks = String(q || "")
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  if (!toks.length) return list;
  return list.filter((o) => {
    const blob = String(o.q || "");
    return toks.every((t) => blob.includes(t));
  });
}

/* 工項狀態／優先級 → StatusChip 的 kind（與 design/ui 的 STATUS / PRIO 註解寫的 .el-tag 修飾詞一致） */

/* ButtonBase 會拿掉瀏覽器預設外框，鍵盤聚焦時補一圈主色框，月格裡的小標、長條才看得出焦點在哪 */
const FOCUS_RING = {
  "&.Mui-focusVisible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: "1px" },
};

/* 主色內框（今天／選取的日格） */
const ring = (w, side = "all") => (t) =>
  side === "bottom" ? `inset 0 -${w}px 0 ${t.palette.primary.main}` : `inset 0 0 0 ${w}px ${t.palette.primary.main}`;

/* 兩個彈出視窗共用的外框：寬度固定、手機靠 max-width:92% 收斂；直向 flex 讓中間清單自己捲 */
const dialogPaper = (w) => ({
  width: w,
  maxWidth: "92%",
  maxHeight: "86vh",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
});

/** 事件類型徽章：色來自 KINDS 而非議題型別 */
function kindChipSx(k) {
  const c = _.get(KIND_MAP, [k, "c"], "#8a94a3");
  return { color: c, bgcolor: `${c}22`, fontWeight: 800 };
}

/**
 * 月格 / 週欄裡的事件小標。
 * 開始事件用外框、截止/里程碑/會議用實心、假日用紅色淡標——
 * 刻意不靠文字顏色深淺區分，因為深色主題下淡色文字會不可讀。
 *
 * 月格的版面常數（SPAN_TOP 等）是用這裡的 px 尺寸算出來的，所以字級、內距維持 px，不跟大字模式縮放。
 */
function evChipSx(ev, dense) {
  /* dense 模式的截斷交給內層 .evchip-t（跑馬燈需要一個可移動的內層），
     這裡只負責把外框鎖成格子寬度、超出的部分裁掉 */
  const base = {
    display: "block",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    textAlign: "left",
    border: "none",
    cursor: "pointer",
    fontSize: "10px",
    fontWeight: 700,
    p: "2px 6px",
    borderRadius: "var(--radius-xs)",
    mt: "3px",
    ...(dense ? {} : { whiteSpace: "normal", lineHeight: 1.45 }),
    ...FOCUS_RING,
  };
  if (ev.kind === "holiday") return { ...base, color: "#d92d20", bgcolor: "rgba(217,45,32,.13)" };
  if (ev.kind === "start")
    return { ...base, color: "text.primary", bgcolor: "background.paper", boxShadow: `inset 0 0 0 1px ${ev.color}` };
  return { ...base, color: "#fff", bgcolor: ev.color, ...(ev.overdue ? { boxShadow: "inset 3px 0 0 #ffd7d3" } : {}) };
}

/**
 * 月檢視的跨日長條。
 * 底色沿用小標的語彙（實心＝專案色），圓角只收在真正的起訖端：
 * 續接到上／下一週、或被月份邊界裁掉的那一側切平，一眼就看得出「這條還沒完」。
 *
 * 左緣 3px 的淡紅色條只給逾期用 —— 這個位置在本頁（evChipSx 與圖例）既有的語彙裡
 * 專指逾期，里程碑不再共用它；里程碑長條的標題本來就以「里程碑：」開頭，不必再搶一個警示槽位。
 */
function spanBarSx(ev, seg) {
  const r0 = seg.roundStart ? "var(--radius-xs)" : "0";
  const r1 = seg.roundEnd ? "var(--radius-xs)" : "0";
  /* 已完成的專案褪一階：顏色仍是該專案的識別色（跟「顯示專案」膠囊對得起來），
     但和還在跑的專案並排時一眼分得出哪些已經結案。 */
  return {
    display: "block",
    boxSizing: "border-box",
    minWidth: 0,
    height: `${SPAN_H}px`,
    lineHeight: `${SPAN_H}px`,
    mx: "3px",
    px: "6px",
    border: "none",
    textAlign: "left",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    cursor: "pointer",
    fontSize: "10px",
    fontWeight: 700,
    color: "#fff",
    bgcolor: ev.color,
    borderRadius: `${r0} ${r1} ${r1} ${r0}`,
    ...(ev.done ? { opacity: 0.55 } : {}),
    ...(ev.overdue ? { boxShadow: "inset 3px 0 0 #ffd7d3" } : {}),
    ...FOCUS_RING,
  };
}

/* ---------- 小元件 ---------- */

/**
 * 月格 / 週欄的事件小標。
 *
 * 格子寬度是固定的（欄位 minmax(0,1fr)），塞不下的標題預設是省略號；
 * 滑鼠移上去才跑馬燈捲一遍再捲回來 —— 全文看得到，又不會整個月曆一直在動。
 *
 * 捲動距離用 JS 量（clientWidth - scrollWidth）再寫進 CSS 變數 --mq：
 * 純 CSS 算不出「這一則溢出了多少」，寫死距離會讓短標題捲出一片空白。
 * 動畫本體在 index.scss 的 .evchip-t.is-marquee / @keyframes elpmMarquee。
 */
function EvChip({ ev, dense, onOpen }) {
  const textRef = useRef(null);
  const [shift, setShift] = useState(0);

  const measure = () => {
    const el = textRef.current;
    if (!el) return;
    const over = el.scrollWidth - el.clientWidth;
    // 差 1~2px 多半是次像素誤差，不值得為它動起來
    setShift(over > 2 ? over : 0);
  };

  return (
    <ButtonBase
      className="evchip"
      onClick={(e) => {
        e.stopPropagation();
        onOpen(ev);
      }}
      onMouseEnter={dense ? measure : undefined}
      onMouseLeave={dense ? () => setShift(0) : undefined}
      onFocus={dense ? measure : undefined}
      onBlur={dense ? () => setShift(0) : undefined}
      title={ev.title}
      sx={evChipSx(ev, dense)}
    >
      <Box
        component="span"
        ref={textRef}
        className={`evchip-t${shift ? " is-marquee" : ""}`}
        sx={
          shift
            ? {
              "--mq": `-${shift}px`,
              // 距離越長跑越久，速度才一致（每秒約 40px），至少 2.4 秒
              animationDuration: `${Math.max(2.4, (shift / 40) * 2).toFixed(1)}s`,
            }
            : undefined
        }
      >
        {ev.title}
      </Box>
    </ButtonBase>
  );
}

EvChip.propTypes = {
  ev: PropTypes.object.isRequired,
  dense: PropTypes.bool,
  onOpen: PropTypes.func,
};
EvChip.defaultProps = { dense: true, onOpen: () => {} };

/**
 * 月檢視的跨日長條（一週一段）。
 *
 * 一段就是覆蓋層裡的一個 grid 項目，靠 gridColumn 橫跨該週的起訖格 ——
 * 覆蓋層與日格用同一組 `repeat(7,minmax(0,1fr))` 欄寬、同樣的左右邊界，
 * 所以第 n 欄一定對得上第 n 天，不必自己換算百分比。
 * 覆蓋層本身 pointer-events:none（點空白處仍是「點那一天」），長條自己收回點擊。
 *
 * 點下去開的是 seg.ymd —— 「這一段看得到的第一天」，不是整條的真起點：
 * 起點在上個月的長條（月檢視只畫得到尾段）若用真起點，setSelDay 設進去的日期不在目前區間內，
 * 會被「換月後關掉視窗」的 effect 立刻收掉，看起來就是這條長條點不開。
 */
function SpanBar({ ev, seg, onOpen }) {
  return (
    <ButtonBase
      title={ev.barTitle}
      onClick={(e) => {
        e.stopPropagation();
        onOpen({ ...ev, ymd: seg.ymd });
      }}
      sx={{
        ...spanBarSx(ev, seg),
        gridColumn: `${seg.c0 + 1} / ${seg.c1 + 2}`,
        gridRow: `${seg.lane + 1}`,
        alignSelf: "start",
        pointerEvents: "auto",
      }}
    >
      {ev.barLabel}
    </ButtonBase>
  );
}

SpanBar.propTypes = {
  ev: PropTypes.object.isRequired,
  seg: PropTypes.object.isRequired,
  onOpen: PropTypes.func,
};
SpanBar.defaultProps = { onOpen: () => {} };

/**
 * 議程列 / 當日視窗列 — 一筆事件的完整內容。
 * 使用者要的是「點那天就看得到每筆工項內容」，所以這裡不只放標題：
 * 事件性質、型別、代碼、名稱、說明、專案、指派人、狀態、優先級、起訖日、進度全部攤開。
 */
function EvDetail({ ev, pad, onOpen }) {
  const kind = KIND_MAP[ev.kind] || KIND_MAP.start;
  const st = STATUS[ev.status];
  const ty = TYPE[ev.issueType];
  const from = ymdLabel(ev.startRaw);
  const to = ymdLabel(ev.endRaw);
  /* 進度可能是 null / 字串 / 超界數字，一律夾回 0–100 才不會畫出負寬度或爆表的長條 */
  const pct =
    ev.progress == null || ev.progress === "" ? null : Math.max(0, Math.min(100, Number(ev.progress) || 0));

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onOpen(ev)}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpen(ev);
      }}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        p: pad,
        borderTop: 1,
        borderColor: "divider",
        cursor: "pointer",
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      {/* 第一列：事件性質（開始／截止／里程碑起／里程碑結束／會議／假日）+ 型別 + 時間 + 代碼 */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        {/* 跨日長條中段那一列（「進行期間」）刻意用中性灰而不是事件類型色：
            這一列是從截止那則展開的（kind 仍是 due／phase），沿用事件類型色會畫成「工項截止」的橘色，
            但橘色在圖例已經指定給截止那件事。 */}
        {ev.spanMid ? (
          <StatusChip kind="closed" size="small">
            {ev.nature || kind.label}
          </StatusChip>
        ) : (
          <Chip size="small" label={ev.nature || kind.label} sx={kindChipSx(ev.kind)} />
        )}
        {ty && (
          <StatusChip kind="type" size="small" title={typeTitle(ev.issueType)}>
            {ty.label}
          </StatusChip>
        )}
        {ev.time && (
          <Typography
            component="span"
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
          >
            {ev.time}
          </Typography>
        )}
        {ev.code && (
          <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
            {ev.code}
          </Typography>
        )}
        {ev.overdue && (
          <StatusChip kind="attention" size="small">
            逾期
          </StatusChip>
        )}
      </Box>

      <Typography sx={{ fontWeight: 700 }}>{ev.name}</Typography>

      {/* 說明只留兩行，一列的高度有限，全文請進工項詳情看 */}
      {ev.desc && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
        >
          {ev.desc}
        </Typography>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        {ev.projectName && (
          <Typography
            component="span"
            variant="body2"
            color="text.secondary"
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, fontWeight: 600 }}
          >
            <Box
              component="span"
              sx={{ width: 9, height: 9, borderRadius: "var(--radius-xs)", bgcolor: ev.color, flexShrink: 0 }}
            />
            {ev.projectName}
          </Typography>
        )}
        {/*
          所屬里程碑。只寫專案名稱時，同一個專案的幾十筆工項在清單上長得一模一樣，
          看不出這件事屬於哪一段。里程碑事件本身不重複標（標題已經寫了「里程碑：…」）。
        */}
        {ev.phaseName && ev.kind !== "phase" && (
          <Chip
            size="small"
            icon={<MdOutlinedFlag />}
            label={ev.phaseName}
            title={`所屬里程碑：${ev.phaseName}`}
            sx={{ maxWidth: "100%", fontWeight: 700 }}
          />
        )}
        {ev.assignee && (
          <Typography
            component="span"
            variant="body2"
            color="text.secondary"
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, fontWeight: 600 }}
          >
            <Avatar
              sx={{
                width: "2em",
                height: "2em",
                fontSize: "calc(var(--t-meta) * 0.6)",
                fontWeight: 700,
                color: "#fff",
                bgcolor: avatarColor(ev.assignee),
              }}
            >
              {initialOf(ev.assignee)}
            </Avatar>
            {ev.assignee}
          </Typography>
        )}
        {st && (
          <StatusChip kind={TASK_STATUS_KIND[ev.status] || "closed"} size="small">
            {st.label}
          </StatusChip>
        )}
        {ev.prio && PRIO[ev.prio] && (
          <StatusChip kind={PRIO_KIND[ev.prio] || "closed"} size="small">
            {PRIO[ev.prio].label}
          </StatusChip>
        )}
      </Box>

      {(from || to || pct != null) && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
          {(from || to) && (
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
            >
              {from || "—"} – {to || "—"}
            </Typography>
          )}
          {pct != null && (
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, minWidth: 96, flex: 1 }}>
              <LinearProgress
                variant="determinate"
                value={pct}
                aria-label={`${pct}%`}
                sx={{
                  flex: 1,
                  minWidth: 52,
                  height: 5,
                  borderRadius: "var(--radius-pill)",
                  bgcolor: "action.hover",
                  "& .MuiLinearProgress-bar": { bgcolor: ev.color, borderRadius: "var(--radius-pill)" },
                }}
              />
              <Typography
                component="span"
                variant="body2"
                sx={{ fontWeight: 800, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
              >
                {pct}%
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

EvDetail.propTypes = {
  ev: PropTypes.object.isRequired,
  pad: PropTypes.string,
  onOpen: PropTypes.func,
};
/* 預設內距是議程清單的；當日視窗傳 "12px 24px"，與 DialogTitle 的水平內距對齊 */
EvDetail.defaultProps = { pad: "11px 14px", onOpen: () => {} };

/**
 * 視窗標頭右上角的關閉鈕（兩個視窗共用）。
 */
function CloseButton({ onClose }) {
  return (
    <Tooltip title="關閉">
      <IconButton aria-label="關閉" onClick={onClose} sx={{ flexShrink: 0, mt: -0.5, mr: -1 }}>
        <MdClose aria-hidden="true" />
      </IconButton>
    </Tooltip>
  );
}

CloseButton.propTypes = { onClose: PropTypes.func };
CloseButton.defaultProps = { onClose: () => {} };

/**
 * 當日詳情視窗的內容（外框 Dialog 在 App 裡）。
 *
 * 原本是釘在右邊的面板，但它固定吃掉 380px，月格被壓到剩一半寬——跨日長條更擠不下；
 * 改成彈出視窗之後月檢視吃得回整個版面寬度。
 *
 * Dialog 本身由 App 掛，這裡只畫內容，並由 App 以 key={ymd} 掛載：
 * 換一天只重新掛載內容（搜尋字串與頁碼自動歸零），Dialog 不會整個關掉再打開。
 * ESC／鎖背景捲動／關閉後還原焦點放在外層（見 App 裡的 dialogOpen effect），
 * 這樣換日期時不會一直重綁事件，也不會在重新掛載的空檔把 body 的 overflow 放掉又鎖回去。
 */
function DayDialog({ ymd, events, isToday, onClose, onOpen }) {
  const [q, setQ] = useState("");
  const list = useMemo(() => matchQuery(events, q), [events, q]);
  /* 傳進去的是「搜尋後」的陣列，總數與翻頁才會反映搜尋結果。
     resetKey 一定要帶：只靠「頁碼夾回有效範圍」不夠——停在第 2 頁時輸入搜尋，
     若命中結果仍有 2 頁以上，頁碼不會動，使用者會看到前 10 筆命中結果被藏起來。
     events.length 也放進 key，是為了切換事件類型／專案／指派人膠囊時同樣回到第 1 頁。 */
  const page = useListPage(list, PAGE_SIZE, `${q}|${events.length}`);
  const d = ymdToDate(ymd);

  return (
    <>
      {/* 直向 flex：標題與搜尋固定，中間清單自己捲，分頁器留在底部不被捲走。 */}
      <DialogTitle
        component="div"
        sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h3" component="h2">
            {d ? `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}` : ymd}
            <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 700, ml: 1 }}>
              週{d ? WEEKDAYS[d.getDay()] : ""}
            </Typography>
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 0.75 }}>
            {isToday && (
              <StatusChip kind="progress" size="small">
                今天
              </StatusChip>
            )}
            <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
              {/* 有搜尋時同時給「符合筆數 / 全部筆數」，才知道是被搜尋濾掉還是當天真的沒事 */}
              {list.length === events.length
                ? `${events.length} 個事件`
                : `${list.length} / ${events.length} 個事件`}
            </Typography>
          </Box>
        </Box>
        <CloseButton onClose={onClose} />
      </DialogTitle>

      {events.length > 0 && (
        <Box sx={{ display: "flex", px: 3, py: 1.5, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
          <SearchField
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="搜尋工項、代碼、專案、指派人…"
            fullWidth
            sx={{ flex: 1, minWidth: 180 }}
          />
        </Box>
      )}

      {/* 清單吃掉視窗剩下的高度並自己捲。min-height:0 一定要寫：
          flex 項目的預設 min-height 是 auto，不歸零的話它會被內容撐開，
          整個視窗長過 86vh、分頁器被推到看不見的地方。 */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        {events.length === 0 && <Empty title="當日無事件" variant="plain" pad="34px 22px" />}
        {events.length > 0 && list.length === 0 && (
          <Empty title="找不到符合的事件" hint="請調整搜尋關鍵字" variant="plain" pad="34px 22px" />
        )}
        {page.pageItems.map((e) => (
          <EvDetail key={e.id} ev={e} pad="12px 24px" onOpen={onOpen} />
        ))}
      </Box>
      {page.pager}
    </>
  );
}

DayDialog.propTypes = {
  ymd: PropTypes.string.isRequired,
  events: PropTypes.array,
  isToday: PropTypes.bool,
  onClose: PropTypes.func,
  onOpen: PropTypes.func,
};
DayDialog.defaultProps = { events: [], isToday: false, onClose: () => {}, onOpen: () => {} };

/**
 * 專案詳情視窗的內容 —— 專案模式點長條時開（外框 Dialog 在 App 裡）。
 *
 * 月曆本身只回答「哪些專案在跑、走到哪、哪些做完了」，工項刻意不畫進格子：
 * 一個月數百則起訖會把版面洗成一片代號，看的人既認不出是哪個專案，
 * 也看不出整體進度。要追細節就從這裡看 —— 同一個專案的工項一次列完，
 * 而不是散在整個月的格子裡、每格還被「+44 更多」蓋掉。
 */
function ProjectDialog({ proj, tasks, onClose }) {
  const [q, setQ] = useState("");
  const list = useMemo(() => {
    const kw = q.trim().toLowerCase();
    if (!kw) return tasks;
    return tasks.filter((t) => `${t.code} ${t.name} ${t.assignee}`.toLowerCase().includes(kw));
  }, [tasks, q]);
  const page = useListPage(list, PAGE_SIZE, `${q}|${tasks.length}`);

  return (
    <>
      <DialogTitle
        component="div"
        sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box component="span" sx={{ width: 11, height: 11, borderRadius: "var(--radius-xs)", bgcolor: proj.color, flexShrink: 0 }} />
            <Typography variant="h3" component="h2">
              {proj.name}
            </Typography>
            <StatusChip kind={proj.done ? "done" : proj.overdue ? "attention" : "progress"} size="small">
              {proj.done ? "已完成" : proj.overdue ? "逾期未完成" : "進行中"}
            </StatusChip>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap", mt: 0.75 }}>
            <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
              {ymdLabel(proj.s)} – {ymdLabel(proj.e)}
            </Typography>
            <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
              共 {proj.total} 項工項 · 已完成 {proj.doneCount} 項
            </Typography>
          </Box>
          {/* 進度條：數字之外再給一條長度，掃一眼就看得出離完成還有多遠 */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
            <LinearProgress
              variant="determinate"
              value={Math.max(0, Math.min(100, Number(proj.pct) || 0))}
              aria-label={`${proj.pct}%`}
              sx={{
                flex: 1,
                height: 7,
                borderRadius: "var(--radius-pill)",
                bgcolor: "action.hover",
                "& .MuiLinearProgress-bar": { bgcolor: proj.color, borderRadius: "var(--radius-pill)" },
              }}
            />
            <Typography
              component="span"
              variant="body2"
              sx={{ fontWeight: 800, fontVariantNumeric: "tabular-nums" }}
            >
              {proj.pct}%
            </Typography>
          </Box>
        </Box>
        <CloseButton onClose={onClose} />
      </DialogTitle>

      {tasks.length > 0 && (
        <Box sx={{ display: "flex", px: 3, py: 1.5, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
          <SearchField
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="搜尋工項、代碼、指派人…"
            fullWidth
            sx={{ flex: 1, minWidth: 180 }}
          />
        </Box>
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        {tasks.length === 0 && <Empty title="此專案於該期間無工項" variant="plain" pad="34px 22px" />}
        {tasks.length > 0 && list.length === 0 && (
          <Empty title="找不到符合的工項" hint="請調整搜尋關鍵字" variant="plain" pad="34px 22px" />
        )}
        {page.pageItems.map((t) => {
          const st = STATUS[t.status];
          return (
            <Box
              key={t.id}
              sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 3, py: 1.5, borderTop: 1, borderColor: "divider" }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  {st && (
                    <StatusChip kind="closed" size="small">
                      {st.label}
                    </StatusChip>
                  )}
                  {t.code && (
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                      {t.code}
                    </Typography>
                  )}
                  {t.overdue && (
                    <StatusChip kind="attention" size="small">
                      逾期
                    </StatusChip>
                  )}
                </Box>
                <Typography sx={{ fontWeight: 700, mt: 0.5 }}>{t.name}</Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  component="div"
                  sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap", mt: 0.5, fontWeight: 600 }}
                >
                  {t.assignee && <span>{t.assignee}</span>}
                  {(t.s || t.e) && (
                    <span>
                      {ymdLabel(t.s)} – {ymdLabel(t.e)}
                    </span>
                  )}
                </Typography>
              </Box>
              <Typography
                component="span"
                variant="body2"
                sx={{ fontWeight: 800, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}
              >
                {t.progress}%
              </Typography>
            </Box>
          );
        })}
      </Box>
      {page.pager}
    </>
  );
}

ProjectDialog.propTypes = {
  proj: PropTypes.object.isRequired,
  tasks: PropTypes.array,
  onClose: PropTypes.func,
};
ProjectDialog.defaultProps = { tasks: [], onClose: () => {} };

/**
 * 議程檢視。
 * 30 天 × 每天數十筆會排出上千列，所以先攤平成事件列分頁，
 * 再把當頁的事件依日期切回原本「日期方塊 + 事件卡」的版型。
 * 由外層以 key={rangeStart} 掛載，換區間時搜尋與頁碼一併歸零。
 */
function AgendaList({ events, holidays, todayYmd, onOpen }) {
  const [q, setQ] = useState("");
  const list = useMemo(() => matchQuery(events, q), [events, q]);
  /* 同 DayDialog：搜尋字串或篩選後的總筆數一變就回到第 1 頁，
     否則停在第 7 頁輸入搜尋、命中結果剛好還有 7 頁以上時，前 60 筆會被跳過。 */
  const page = useListPage(list, PAGE_SIZE, `${q}|${events.length}`);

  /* 當頁事件依日期切段（events 已按 ymd 排序，所以只要比對前一段即可） */
  const groups = useMemo(() => {
    const out = [];
    page.pageItems.forEach((e) => {
      const last = out[out.length - 1];
      if (last && last.ymd === e.ymd) last.events.push(e);
      else out.push({ ymd: e.ymd, events: [e] });
    });
    return out;
  }, [page.pageItems]);

  /* 某天可能被切在兩頁之間，標題要能說明「本頁 3 筆 · 當日共 12 筆」 */
  const dayTotal = useMemo(() => _.countBy(list, "ymd"), [list]);

  return (
    <div>
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", px: 2.5, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
        <SearchField
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="搜尋工項、代碼、專案、指派人…"
          fullWidth
          sx={{ flex: 1, minWidth: 180 }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
          {list.length === events.length ? `${events.length} 筆` : `${list.length} / ${events.length} 筆`}
        </Typography>
      </Box>

      {events.length === 0 && (
        <Empty title="此期間無事件" hint="請調整事件類型、專案或指派人篩選條件" pad="56px 20px" />
      )}
      {events.length > 0 && list.length === 0 && (
        <Empty title="找不到符合的事件" hint="請調整搜尋關鍵字" pad="56px 20px" />
      )}

      {groups.map((g) => {
        const d = ymdToDate(g.ymd);
        const holiday = holidays[g.ymd] || "";
        const today = g.ymd === todayYmd;
        const total = dayTotal[g.ymd] || g.events.length;
        return (
          <Box key={g.ymd} sx={{ display: "flex", gap: 1.75, px: 2.5, py: 1.75, borderBottom: 1, borderColor: "divider" }}>
            {/* 日期方塊（同「國定假日」設定頁的 44px 圓角方塊；大字模式會跟著字撐大） */}
            <Box sx={{ minWidth: 44, flexShrink: 0 }}>
              <Box
                sx={{
                  minWidth: 44,
                  minHeight: 44,
                  px: 0.5,
                  boxSizing: "border-box",
                  borderRadius: "var(--radius-sm)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  ...(today
                    ? { bgcolor: "primary.light", color: "primary.main" }
                    : holiday
                    ? { bgcolor: "error.light", color: "error.main" }
                    : { bgcolor: "action.hover", color: "text.secondary" }),
                }}
              >
                <Box component="span" sx={{ fontSize: "var(--t-body)", fontWeight: 900, lineHeight: 1 }}>
                  {d ? d.getDate() : ""}
                </Box>
                <Box component="span" sx={{ fontSize: "calc(var(--t-meta) * 0.7)", fontWeight: 700, mt: "2px" }}>
                  {d ? d.getMonth() + 1 : ""}月
                </Box>
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 700, textAlign: "center", mt: 0.5 }}
              >
                週{d ? WEEKDAYS[d.getDay()] : ""}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, bgcolor: "background.paper", border: 1, borderColor: "divider" }}>
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
                sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", px: 1.75, py: 1.125, fontWeight: 700 }}
              >
                {today && (
                  <StatusChip kind="progress" size="small">
                    今天
                  </StatusChip>
                )}
                {holiday && (
                  <StatusChip kind="attention" size="small">
                    {holiday}
                  </StatusChip>
                )}
                <span>
                  {g.events.length === total ? `${total} 個事件` : `本頁 ${g.events.length} 筆 · 當日共 ${total} 筆`}
                </span>
              </Typography>
              {g.events.map((e) => (
                <EvDetail key={e.id} ev={e} onOpen={onOpen} />
              ))}
            </Box>
          </Box>
        );
      })}
      {page.pager}
    </div>
  );
}

AgendaList.propTypes = {
  events: PropTypes.array,
  holidays: PropTypes.object,
  todayYmd: PropTypes.string,
  onOpen: PropTypes.func,
};
AgendaList.defaultProps = { events: [], holidays: {}, todayYmd: "", onOpen: () => {} };

/* ---------- 主頁面 ---------- */

function App(props) {
  /* anchor 是目前聚焦的日期：月檢視只看 y/m，週檢視取所在週，議程檢視由此往後 30 天 */
  const [anchor, setAnchor] = useState(() => {
    const n = new Date();
    return { y: n.getFullYear(), m: n.getMonth(), d: n.getDate() };
  });
  const [view, setView] = useState("month");
  /* 預設專案粒度：見 SCOPES 的說明 */
  const [scope, setScope] = useState("project");
  /* 專案模式點長條要開的專案（存 projKey，資料每次重算也不會抓到舊物件） */
  const [selProject, setSelProject] = useState(null);
  /* RD 模式點某人的小標時，記住是哪個人 —— 當日視窗要只列他那天的工項，
     不是把全部人的都攤出來。null = 沒有指定人（點日期空白處進來的） */
  const [selPerson, setSelPerson] = useState(null);
  const [calHidden, setCalHidden] = useState([]);
  const [hiddenKinds, setHiddenKinds] = useState([]);
  const [assigneeFilter, setAssigneeFilter] = useState(ALL_ASSIGNEE);
  /* 被點開的日期（ymd），null 表示沒開視窗。
     改成彈出視窗之後不再預設選今天：一進頁面就被一張遮罩擋住月曆，比看不到入口更糟。
     格子本身有 cursor:pointer 與「查看當日議程」的 title，點得開這件事不必靠預設開啟來提示。 */
  const [selDay, setSelDay] = useState(null);
  /* 假日累積表：ymd → 名稱。reducer 只存單一年份，跨年區間要靠這裡累積 */
  const [holidayMap, setHolidayMap] = useState({});

  const todayYmd = useMemo(() => ymdKey(new Date()), []);
  const anchorDate = useMemo(() => new Date(anchor.y, anchor.m, anchor.d), [anchor]);

  /* 目前檢視要涵蓋的日期區間 */
  const range = useMemo(() => {
    if (view === "week") {
      const s = addDays(anchorDate, -anchorDate.getDay());
      return { s, e: addDays(s, 6) };
    }
    if (view === "agenda") {
      return { s: anchorDate, e: addDays(anchorDate, AGENDA_DAYS - 1) };
    }
    return { s: new Date(anchor.y, anchor.m, 1), e: new Date(anchor.y, anchor.m + 1, 0) };
  }, [view, anchor, anchorDate]);

  const rangeStart = ymdKey(range.s);
  const rangeEnd = ymdKey(range.e);

  useEffect(() => {
    props.GET_ProjectList({ Page: 0, PageCount: 0 }, null, null);
    // Calendar API 尚未回資料時，用工項清單自行組事件當備援；
    // 另外 Calendar API 不含 issue_key / issue_type，也靠這份清單補齊型別徽章與代碼
    props.GET_ScheduleWorkItems({}, null, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    /* 後端是「起或迄落在區間內」的寬鬆過濾，所以有一端落在本區間的跨月工項抓得到。
       已知缺口：起訖「整段包住」本區間的工項（例如 6/20 → 8/10 在 7 月檢視）兩端都不在區間內，
       後端不會回，月曆上也就畫不出長條。要補得動抓取區間或 API，本次刻意不動。 */
    props.GET_ScheduleCalendar({ startDate: rangeStart, endDate: rangeEnd }, null, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeStart, rangeEnd]);

  /* 假日一次只能問一年（saga 是 takeLatest 且 reducer 只有一個 List），
     所以用 requestedYears 記錄已送出的年份，靠 callback 一年一年串起來。 */
  const requestedYears = useRef([]);
  const [yearTick, setYearTick] = useState(0);
  useEffect(() => {
    const years = _.uniq([range.s.getFullYear(), range.e.getFullYear()]);
    const next = years.find((yy) => !requestedYears.current.includes(yy));
    if (next == null) return;
    requestedYears.current.push(next);
    props.GET_HolidayList({ year: next }, () => setYearTick((t) => t + 1), null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeStart, rangeEnd, yearTick]);

  const { projectList, calendarEvents, workItems, holidayList } = props;

  /* 把 store 裡的假日併進累積表；沒有新資料就回傳原物件，避免無限 re-render */
  useEffect(() => {
    const list = toArray(holidayList);
    if (!list.length) return;
    setHolidayMap((prev) => {
      let changed = false;
      const next = { ...prev };
      list.forEach((h) => {
        const ymd = ymdOf(_.get(h, "date", _.get(h, "Date", "")));
        if (!ymd) return;
        const name = _.get(h, "name", _.get(h, "Name", "")) || "國定假日";
        if (next[ymd] !== name) {
          next[ymd] = name;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [holidayList]);

  /* 後端可能回陣列，也可能包成 { data_list } */
  const projects = useMemo(() => toArray(projectList).map(normalizeProject), [projectList]);

  /* 工項清單 → id 對照表，用來補 Calendar API 缺的 issue_key / issue_type */
  const taskMeta = useMemo(() => {
    const map = {};
    toArray(workItems).forEach((w) => {
      const t = normalizeTask(w);
      if (t.id != null) map[String(t.id)] = t;
    });
    return map;
  }, [workItems]);

  /* 統一成本頁用的事件形狀：Calendar API 為主，沒資料時退回工項清單 */
  const sourceEvents = useMemo(() => {
    const cal = toArray(calendarEvents);
    if (cal.length) {
      return cal.map((e) => ({
        id: _.get(e, "id", _.get(e, "Id")),
        type: _.get(e, "type", _.get(e, "Type", "task")),
        title: _.get(e, "title", _.get(e, "Title", "")) || "",
        code: _.get(e, "issue_key", _.get(e, "issueKey", _.get(e, "code", ""))) || "",
        projectId: _.get(e, "project_id", _.get(e, "projectId")),
        projectName: _.get(e, "project_name", _.get(e, "projectName", "")) || "",
        /* 所屬里程碑。只有專案名稱時，同一個專案的幾十筆工項在清單上長得一模一樣，
           分不出是哪一段的事；Calendar API 沒回時退回工項清單裡的 phase_name */
        phaseName:
          _.get(e, "phase_name", _.get(e, "phaseName", "")) ||
          _.get(taskMeta, [String(_.get(e, "id", _.get(e, "Id"))), "raw", "phase_name"], "") ||
          "",
        start: _.get(e, "start_date", _.get(e, "startDate")),
        end: _.get(e, "end_date", _.get(e, "endDate")),
        status: _.get(e, "status", _.get(e, "Status")),
        assigneeAccount: _.get(e, "assignee", _.get(e, "Assignee", "")) || "",
        assigneeName: _.get(e, "assignee_name", _.get(e, "assigneeName", "")) || "",
        issueType: _.get(e, "issue_type", _.get(e, "issueType", "")),
        /* 當日視窗要顯示優先級與進度；Calendar API 兩者都有，缺欄位時留 null 讓 UI 整塊不出現 */
        priority: _.get(e, "priority", _.get(e, "Priority", null)),
        progress: _.get(e, "progress", _.get(e, "Progress", null)),
      }));
    }
    return toArray(workItems)
      .map(normalizeTask)
      .map((t) => ({
        id: t.id,
        type: "task",
        title: t.name,
        code: t.code,
        projectId: t.projectId,
        projectName: t.projectName,
        phaseName: _.get(t, "raw.phase_name", _.get(t, "raw.phaseName", "")) || "",
        start: t.startRaw,
        end: t.dueRaw,
        status: t.status,
        assigneeAccount: t.assigneeAccount,
        assigneeName: t.assignee,
        issueType: t.type,
        priority: t.prio,
        progress: t.progress,
      }));
  }, [calendarEvents, workItems, taskMeta]);

  /* 顯示專案膠囊：專案清單為主，清單還沒回來就從事件推導 */
  const calProjects = useMemo(() => {
    const seen = [];
    const push = (key, name) => {
      if (!key || seen.some((x) => x.key === key)) return;
      seen.push({ key, name });
    };
    projects.forEach((p) => push(String(p.id != null ? p.id : p.name), p.name));
    sourceEvents.forEach((e) =>
      push(String(e.projectId != null ? e.projectId : e.projectName), e.projectName)
    );
    return seen
      .filter((x) => x.name)
      .map((x, i) => ({ ...x, c: PALETTE[i % PALETTE.length] }));
  }, [projects, sourceEvents]);

  const calColor = useMemo(() => {
    const map = {};
    calProjects.forEach((p) => {
      map[p.key] = p.c;
    });
    return map;
  }, [calProjects]);

  /**
   * 把事件攤平成「某一天的一則事件」。
   * 工項的起、迄各算一則（設計稿的 `XX 開始` / `XX 截止`），里程碑同理。
   *
   * 起訖兩則會帶同一個 pairKey 與 edge（s／e）：月檢視要把「同一件事的起與迄」重新配對
   * 成一條跨日長條，靠這兩個欄位就能在篩選之後才配對，不必回頭再查一次原始事件。
   */
  const occurrences = useMemo(() => {
    const out = [];
    sourceEvents.forEach((e, idx) => {
      const projKey = String(e.projectId != null ? e.projectId : e.projectName);
      const color = calColor[projKey] || "#8a94a3";
      const sYmd = ymdOf(e.start);
      const eYmd = ymdOf(e.end);
      const base = {
        projKey,
        projectId: e.projectId,
        projectName: e.projectName,
        phaseName: e.phaseName || "",
        color,
        assigneeKey: e.assigneeAccount || "",
        assignee: e.assigneeName || "",
        status: "",
        prio: "",
        code: "",
        desc: "",
        progress: null,
        /* 當日視窗要顯示「起訖日」，月檢視也要靠它畫跨日長條，
           所以每則事件都留著整段區間，而不只是自己那一天 */
        startRaw: e.start,
        endRaw: e.end,
        overdue: false,
      };

      if (e.type === "phase") {
        /* 後端 ScheduleService 在標題前硬加「[交付目標] 」（更早是「[階段] 」）——這是比對用的前綴，三種寫法都剝掉 */
        const name = String(e.title || "").replace(/^\[(?:交付目標|里程碑|階段)\]\s*/, "");
        if (sYmd) out.push({ ...base, id: `p${idx}s`, pairKey: `p${idx}`, edge: "s", kind: "phase", nature: "里程碑開始", ymd: sYmd, time: timeOf(e.start), title: `里程碑：${name}`, name });
        if (eYmd) out.push({ ...base, id: `p${idx}e`, pairKey: `p${idx}`, edge: "e", kind: "phase", nature: "里程碑結束", ymd: eYmd, time: timeOf(e.end), title: `里程碑結束：${name}`, name });
        return;
      }
      if (e.type === "meeting") {
        const ymd = sYmd || eYmd;
        if (ymd) out.push({ ...base, id: `m${idx}`, kind: "meeting", nature: "會議", ymd, time: timeOf(e.start || e.end), title: `會議：${e.title}`, name: e.title });
        return;
      }

      /* 只有工項才查 taskMeta：里程碑事件的 id 與工項 id 是不同序列，會誤撞 */
      const meta = taskMeta[String(e.id)] || {};
      const code = meta.code || e.code || "";
      const status = statusKey(e.status);
      const label = code || e.title;
      const prioRaw = e.priority != null ? e.priority : meta.prio;
      const progressRaw = e.progress != null ? e.progress : meta.progress;
      const task = {
        ...base,
        taskId: e.id,
        code,
        status,
        /* 型別未知時留空字串，讓徽章整個不出現；硬套 typeKey 會把所有工項標成「任務」 */
        issueType: meta.type || (e.issueType ? typeKey(e.issueType) : ""),
        assignee: e.assigneeName || meta.assignee || "",
        assigneeKey: e.assigneeAccount || meta.assigneeAccount || "",
        /* Calendar API 沒有描述，靠工項清單補；當日視窗要「看那天詳細說明」就靠這欄 */
        desc: plainText(meta.desc),
        /* prioKey(null) 會給 medium，所以先確認真的有值再轉，避免全部工項假裝成「一般」 */
        prio: prioRaw == null || prioRaw === "" ? "" : prioKey(prioRaw),
        progress: progressRaw == null || progressRaw === "" ? null : progressRaw,
      };
      if (sYmd) out.push({ ...task, id: `t${idx}s`, pairKey: `t${idx}`, edge: "s", kind: "start", nature: "工項開始", ymd: sYmd, time: timeOf(e.start), title: `${label} 開始`, name: e.title });
      if (eYmd) {
        out.push({
          ...task,
          id: `t${idx}e`,
          pairKey: `t${idx}`,
          edge: "e",
          kind: "due",
          nature: "工項截止",
          ymd: eYmd,
          time: timeOf(e.end),
          title: `${label} 截止`,
          name: e.title,
          overdue: isOverdue(e.status, e.end),
        });
      }
    });

    Object.keys(holidayMap).forEach((ymd) => {
      out.push({
        id: `h${ymd}`,
        kind: "holiday",
        nature: "假日",
        ymd,
        time: "",
        title: holidayMap[ymd],
        name: holidayMap[ymd],
        color: "#d92d20",
        projKey: "",
        projectName: "",
        phaseName: "",
        assignee: "",
        assigneeKey: "",
        status: "",
        prio: "",
        desc: "",
        progress: null,
        startRaw: "",
        endRaw: "",
        overdue: false,
      });
    });
    /* 模糊搜尋的比對字串預先算好：搜尋是每個 keystroke 都跑，
       在這裡算一次比在 filter 裡重組數千條字串便宜得多 */
    return out.map((o) => ({ ...o, q: searchBlob(o) }));
  }, [sourceEvents, calColor, taskMeta, holidayMap]);

  /**
   * 專案層級的彙總（專案模式的資料來源）。
   *
   * 進度是「由底下工項推估」而不是讀專案自己的 progress 欄位：專案那個數字要有人
   * 回頭維護才準，而工項狀態是每天在動的，推估出來的才對得上實際進度。
   * 算法是每項工項的進度取平均，已完成／已關閉一律算 100 ——
   * 只數「完成幾項」會讓一個做到 90% 的工項跟還沒開工的一樣是 0，
   * 只讀 progress 又會讓已完成但沒人拉過進度條的工項永遠停在 0。
   *
   * 起訖取該專案所有工項與里程碑的最早開始、最晚截止（專案本身沒有手填的起訖日，
   * 見 resources/forms.js 的說明），所以長條畫的就是「這個專案實際佔掉的期間」。
   */
  const projectRows = useMemo(() => {
    const map = new Map();
    sourceEvents.forEach((e) => {
      /* 會議不代表工作量，納進來會讓沒有工項、只開過會的專案顯示一條 0% 的長條 */
      if (e.type === "meeting") return;
      const key = String(e.projectId != null ? e.projectId : e.projectName);
      if (!key || key === "undefined" || key === "null") return;
      const row = map.get(key) || {
        key,
        name: e.projectName || "",
        s: "",
        e: "",
        total: 0,
        doneCount: 0,
        sum: 0,
      };
      const sY = ymdOf(e.start);
      const eY = ymdOf(e.end);
      if (sY && (!row.s || sY < row.s)) row.s = sY;
      if (eY && (!row.e || eY > row.e)) row.e = eY;
      /* 里程碑只用來撐起訖區間，不計進進度：它本身沒有進度，算進去會稀釋掉工項的真實比例 */
      if (e.type !== "phase") {
        const st = statusKey(e.status);
        const finished = st === "done" || st === "closed";
        row.total += 1;
        if (finished) row.doneCount += 1;
        row.sum += finished ? 100 : Math.max(0, Math.min(100, Number(e.progress) || 0));
      }
      if (!row.name) row.name = e.projectName || "";
      map.set(key, row);
    });

    /* 專案清單只用來補名稱與「完全沒有工項時」的退路，不覆蓋推估值 */
    const byKey = {};
    projects.forEach((p) => {
      byKey[String(p.id != null ? p.id : p.name)] = p;
    });

    return [...map.values()]
      .filter((r) => r.s && r.e)
      .map((r) => {
        const p = byKey[r.key];
        const pct = r.total ? Math.round(r.sum / r.total) : Math.round(Number(_.get(p, "pct", 0)) || 0);
        /* 「做完了」以工項為準：全部工項都結案才算完成。沒有工項時退回專案自己的狀態，
           否則一個剛開好、還沒建工項的專案會因為 0/0 被當成已完成。 */
        const done = r.total > 0 ? r.doneCount === r.total : _.get(p, "status") === "done";
        return {
          ...r,
          name: r.name || _.get(p, "name", "") || "未命名專案",
          pct: Math.max(0, Math.min(100, pct)),
          done,
          color: calColor[r.key] || "#8a94a3",
          overdue: !done && !!r.e && r.e < todayYmd,
        };
      });
  }, [sourceEvents, projects, calColor, todayYmd]);

  /* 專案 → 該專案的工項列（專案詳情視窗用；月曆格子上不畫工項） */
  const projectTasks = useMemo(() => {
    const map = {};
    sourceEvents.forEach((e) => {
      if (e.type === "phase" || e.type === "meeting") return;
      const key = String(e.projectId != null ? e.projectId : e.projectName);
      if (!key || key === "undefined" || key === "null") return;
      const meta = taskMeta[String(e.id)] || {};
      const st = statusKey(e.status);
      const finished = st === "done" || st === "closed";
      (map[key] = map[key] || []).push({
        id: e.id,
        code: meta.code || e.code || "",
        name: e.title || "",
        status: st,
        assignee: e.assigneeName || meta.assignee || "",
        s: ymdOf(e.start),
        e: ymdOf(e.end),
        progress: finished ? 100 : Math.max(0, Math.min(100, Number(e.progress) || 0)),
        overdue: isOverdue(e.status, e.end),
      });
    });
    /* 未完成的排前面（要追的是這些），再按截止日 —— 沒有截止日的排最後 */
    Object.keys(map).forEach((k) => {
      map[k].sort((a, b) => {
        const fa = a.status === "done" || a.status === "closed" ? 1 : 0;
        const fb = b.status === "done" || b.status === "closed" ? 1 : 0;
        if (fa !== fb) return fa - fb;
        return String(a.e || "9999").localeCompare(String(b.e || "9999"));
      });
    });
    return map;
  }, [sourceEvents, taskMeta]);

  /**
   * 專案模式的事件：每天每個專案一列，不是一條橫跨整段期間的長條。
   *
   * 為什麼不再畫連續長條：一條從 8/2 拉到 8/28 的長條只說得出「這個案子在跑」，
   * 但它在每一週列都重畫一次、佔滿整排，卻沒有回答「今天到底動了什麼」。
   * 改成每天一列之後，空白的日子自己會說話 —— 那天沒有人動這個案子。
   *
   * 刻意不給 pairKey / edge：monthSpans 是靠「同一個 pairKey 的 s 與 e 配對」
   * 才畫得出長條，少了它們就自然退回單日小標，不必再去改繪製那一段。
   *
   * 「處理 N」算的是當天開始的工項（與 RD 檢視、後端漏填偵測同一個口徑：start_date），
   * 「未完成 M」只算那 N 件裡面還沒結案的 —— 不是整個專案的待辦總數，
   * 否則每一天都會印出同一個數字，等於沒有資訊。
   */
  const projectOccurrences = useMemo(() => {
    /* (日期, 專案) → 當天開始的工項。只取 start，截止是承諾不是當天的產出，
       而且起訖都算會讓同一件跨日工項被重複計。 */
    const bucket = new Map();
    occurrences.forEach((o) => {
      if (o.kind !== "start" || !o.ymd || !o.projKey) return;
      const id = `${o.ymd}|${o.projKey}`;
      const hit = bucket.get(id);
      const unfinished = o.status !== "done" && o.status !== "closed";
      if (hit) {
        hit.count += 1;
        if (unfinished) hit.open += 1;
        hit.items.push(o.name || o.title || "");
        return;
      }
      bucket.set(id, {
        ymd: o.ymd,
        projKey: o.projKey,
        count: 1,
        open: unfinished ? 1 : 0,
        items: [o.name || o.title || ""],
      });
    });

    const rowByKey = {};
    projectRows.forEach((r) => {
      rowByKey[r.key] = r;
    });

    const out = [...bucket.values()]
      .sort((a, b) => a.ymd.localeCompare(b.ymd) || String(a.projKey).localeCompare(String(b.projKey)))
      .map((b) => {
        const r = rowByKey[b.projKey];
        const name = r?.name || b.projKey;
        return {
          id: `pj-${b.ymd}-${b.projKey}`,
          kind: "start",
          nature: "當日進度",
          ymd: b.ymd,
          time: "",
          /* 與 RD 粒度用同一種寫法：只寫「處理 N」看不出做完沒，
             而全做完和全卡著在這個視角是相反的兩件事。小標一格只有幾十 px，
             數字直接寫出來，不要求使用者點開才知道。 */
          title: `${name} · 完成 ${b.count - b.open} / 未完成 ${b.open}`,
          name,
          color: r?.color || "#8a94a3",
          projKey: b.projKey,
          projectId: b.projKey,
          projectName: name,
          phaseName: "",
          assigneeKey: "",
          assignee: "",
          status: "",
          prio: "",
          code: "",
          issueType: "",
          desc:
            b.items.length > 5
              ? `${b.items.slice(0, 5).join("、")}…等 ${b.items.length} 件`
              : b.items.join("、"),
          progress: null,
          startRaw: b.ymd,
          endRaw: b.ymd,
          overdue: false,
          count: b.count,
          openCount: b.open,
        };
      });

    const holidays = occurrences.filter((o) => o.kind === "holiday");
    return [...out, ...holidays].map((o) => ({ ...o, q: searchBlob(o) }));
  }, [projectRows, occurrences]);

  /*
   * RD 粒度的事件：一天一個人一列。彙總邏輯是純函式（rdSummary.js），
   * 形狀刻意做成與工項事件一致，所以月／週／議程／當日視窗全都不用改就能用。
   * 假日直接沿用工項模式已經算好的那幾則，不重算。
   */
  const rdOccurrences = useMemo(() => {
    const rows = buildRdRows(occurrences, PALETTE);
    const holidays = occurrences.filter((o) => o.kind === "holiday");
    return [...rows, ...holidays].map((o) => ({ ...o, q: searchBlob(o) }));
  }, [occurrences]);

  const activeOcc =
    scope === "project" ? projectOccurrences : scope === "rd" ? rdOccurrences : occurrences;

  /* 指派人下拉的選項由事件推導（不打 /Auth/Admin/List，一般 PM 會 403） */
  const assigneeOpts = useMemo(() => {
    const map = new Map();
    let hasNone = false;
    occurrences.forEach((o) => {
      if (o.kind === "holiday") return;
      if (o.assigneeKey) map.set(o.assigneeKey, o.assignee || o.assigneeKey);
      else hasNone = true;
    });
    return { list: [...map.entries()].map(([k, name]) => ({ k, name })), hasNone };
  }, [occurrences]);

  /* 篩選條件變動後若選項已消失，退回「全部指派人」，避免畫面莫名全空 */
  useEffect(() => {
    if (assigneeFilter === ALL_ASSIGNEE) return;
    if (assigneeFilter === NO_ASSIGNEE && assigneeOpts.hasNone) return;
    if (assigneeOpts.list.some((a) => a.k === assigneeFilter)) return;
    setAssigneeFilter(ALL_ASSIGNEE);
  }, [assigneeOpts, assigneeFilter]);

  const calShown = (key) => !calHidden.includes(key);
  const kindShown = (k) => !hiddenKinds.includes(k);

  const toggleProj = (key) =>
    setCalHidden((s) => (s.includes(key) ? s.filter((x) => x !== key) : [...s, key]));
  const toggleKind = (k) =>
    setHiddenKinds((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));

  const filtered = useMemo(
    () =>
      activeOcc.filter((o) => {
        /* 假日不屬於任何專案也沒有指派人，只受自己的膠囊控制 */
        if (o.kind === "holiday") return kindShown("holiday");
        /* 事件類型與指派人是工項粒度的篩選，專案模式沒有那兩列 UI，也就不該還在背後生效：
           否則在工項模式關掉「工項開始」再切過去，長條會莫名其妙少一半而且找不到原因 */
        if (scope === "task") {
          if (!kindShown(o.kind)) return false;
          if (assigneeFilter !== ALL_ASSIGNEE && (o.assigneeKey || NO_ASSIGNEE) !== assigneeFilter) return false;
        }
        if (!calShown(o.projKey)) return false;
        return true;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeOcc, scope, hiddenKinds, calHidden, assigneeFilter]
  );

  /* 依日期彙整（對應設計稿 calE / addE，只是鍵從「日」換成 ymd） */
  const byDay = useMemo(() => {
    const map = {};
    filtered.forEach((o) => {
      (map[o.ymd] = map[o.ymd] || []).push(o);
    });
    Object.keys(map).forEach((k) => {
      map[k].sort((a, b) => {
        const ka = KIND_ORDER[a.kind] ?? 9;
        const kb = KIND_ORDER[b.kind] ?? 9;
        if (ka !== kb) return ka - kb;
        return String(a.time).localeCompare(String(b.time));
      });
    });
    return map;
  }, [filtered]);

  /**
   * 起訖成對、而且真的跨日的事件 → 一條長條（月檢視用）。
   *
   * 從 filtered 推導而不是回頭讀 sourceEvents：事件類型／專案／指派人的篩選已經套在
   * filtered 上，長條自然跟著篩選走。只剩一端沒被濾掉（例如關掉「工項開始」）時就配不成對，
   * 那一端維持原本的單日小標，不會憑空冒出一條看起來還在進行的長條。
   *
   * absorbed 記下被長條吃掉的那兩則事件 id，月格才不會同一件事又畫長條又畫兩個小標。
   * 只影響月檢視的格子；當日視窗與議程仍然看得到「開始」「截止」兩列。
   */
  const monthSpans = useMemo(() => {
    const pairs = new Map();
    filtered.forEach((o) => {
      if (!o.pairKey) return;
      const p = pairs.get(o.pairKey) || {};
      p[o.edge] = o;
      pairs.set(o.pairKey, p);
    });

    const list = [];
    const absorbed = new Set();
    pairs.forEach((p) => {
      /* 只有一端、同一天、或結束早於開始（資料有誤），一律維持單日小標 */
      if (!p.s || !p.e || p.s.ymd >= p.e.ymd) return;
      absorbed.add(p.s.id);
      absorbed.add(p.e.id);
      /* spanLabel 是「這條長條要寫什麼」的覆寫：專案模式要的是「專案名稱 · 進度%」，
         不是工項模式那套「代碼 + 標題」。由產生事件的那一端決定，這裡不再判斷粒度。 */
      const label =
        p.e.spanLabel ||
        (p.s.kind === "phase" ? p.s.title : [p.e.code, p.e.name].filter(Boolean).join(" ") || p.e.name);
      list.push({
        ...p.e,
        id: `sp-${p.s.id}`,
        /* 這裡的 ymd 是整條的真起點（資料語意：這件事什麼時候開始），搜尋字串與起訖顯示都靠它。
           點下去要開哪一天不看它 —— 由 SpanBar 用「該段在畫面上的第一天」決定，
           因為真起點可能在上個月，那個日期不在目前區間內，視窗會被 out-of-range 的 effect 立刻收掉。 */
        ymd: p.s.ymd,
        s: p.s.ymd,
        e: p.e.ymd,
        barLabel: label,
        barTitle: `${label}（${ymdLabel(p.s.ymd)} – ${ymdLabel(p.e.ymd)}）`,
      });
    });
    return { list, absorbed };
  }, [filtered]);

  /*
   * 目前區間內的事件數（標題右邊的計數）。
   * 起訖兩端都在區間外、但整段蓋過整個區間的長條（例如 6/20 → 8/10 看 7 月），
   * 一則 occurrence 都不會落在區間內：只數 occurrence 會顯示「0 個事件」並跳出
   * 「沒有符合篩選的事件」，但格子上整片都是長條。這種長條補算一則。
   */
  const rangeCount = useMemo(
    () =>
      filtered.filter((o) => o.ymd >= rangeStart && o.ymd <= rangeEnd).length
      + monthSpans.list.filter((sp) => sp.s < rangeStart && sp.e > rangeEnd).length,
    [filtered, monthSpans, rangeStart, rangeEnd]
  );

  /*
   * 專案模式的計數單位是「專案」而不是事件：同一個專案在一個月內會有很多天的列，
   * 沿用 rangeCount 等於把天數當成專案數。
   *
   * 從 filtered 推導，而不是拿 projectRows 的起訖去算重疊。
   * 改成每日列之後，一個專案的期間可能橫跨本月、當月卻一天都沒有動靜
   * （它的工項都開在別的月份）—— 那種情況格子裡一列都沒有，
   * 標題卻寫著「3 個專案」，數字和畫面自己打架。
   */
  const projectsInRange = useMemo(
    () =>
      new Set(
        filtered
          .filter((o) => o.kind !== "holiday" && o.projKey && o.ymd >= rangeStart && o.ymd <= rangeEnd)
          .map((o) => o.projKey)
      ).size,
    [filtered, rangeStart, rangeEnd]
  );
  const emptyRange = scope === "project" ? projectsInRange === 0 : rangeCount === 0;

  const selProjectRow = useMemo(
    () => (selProject ? projectRows.find((r) => r.key === selProject) || null : null),
    [selProject, projectRows]
  );

  /**
   * 假日在月／週格是「當日裝飾」（日期轉紅 + 紅色小標籤），
   * 所以格子裡不再重複列一則假日事件；議程與當日視窗則以事件列呈現。
   * 關掉「假日」膠囊時裝飾也要一起消失，才對得上使用者的預期。
   */
  const holidayOn = !hiddenKinds.includes("holiday");
  const dayHoliday = (ymd) => (holidayOn ? holidayMap[ymd] || "" : "");
  const gridEvents = (ymd) => (byDay[ymd] || []).filter((e) => e.kind !== "holiday");

  /**
   * 月曆格子：前置空格 + 當月天數（與設計稿一致，不補月末空格），再依週切成一列一列。
   *
   * 跨日長條要橫跨同一週的多格，所以版面必須以「週」為單位：先把該週 7 格的日期鍵排成陣列
   * （空格是 null），逐條算出這一週要畫到第幾格，再分層疊起來。
   * 分層只在該週內有效——下一週重算，長條自然接續而不必記住上一週的層號。
   */
  const monthWeeks = useMemo(() => {
    const { y, m } = anchor;
    const firstDow = new Date(y, m, 1).getDay();
    const dim = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDow; i += 1) {
      cells.push({ key: `blank-${i}`, blank: true });
    }
    for (let d = 1; d <= dim; d += 1) {
      const ymd = `${y}-${pad2(m + 1)}-${pad2(d)}`;
      const dow = (firstDow + d - 1) % 7;
      cells.push({
        key: ymd,
        ymd,
        day: d,
        weekend: dow === 0 || dow === 6,
        holiday: dayHoliday(ymd),
        today: ymd === todayYmd,
        /* 已經被長條吸收的起訖事件不再畫小標，否則同一件事會在格子裡出現兩次 */
        singles: gridEvents(ymd).filter((e) => !monthSpans.absorbed.has(e.id)),
      });
    }

    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) {
      const row = cells.slice(i, i + 7);
      const ymds = row.map((c) => (c.blank ? null : c.ymd));
      const segs = [];
      monthSpans.list.forEach((ev) => {
        const seg = weekSegment(ev.s, ev.e, ymds);
        if (seg) segs.push({ ...seg, ev });
      });
      /* 起點早的先放；同起點時長的先放，短的才填得進同一層剩下的空隙。
         最後用 id 打破平手，同一份資料每次排出來的層號才一樣（不會 render 一次跳一次）。 */
      segs.sort((a, b) => a.c0 - b.c0 || b.c1 - a.c1 || String(a.ev.id).localeCompare(String(b.ev.id)));
      const packed = packLanes(segs);
      const laneCap = scope === "project" ? MAX_LANES_PROJECT : MAX_LANES;
      const bars = packed.filter((s) => s.lane < laneCap);
      const hidden = packed.filter((s) => s.lane >= laneCap);

      weeks.push({
        key: row[0].key,
        bars,
        cells: row.map((c, ci) => {
          if (c.blank) return c;
          /* 這一格要讓開幾層：只算「蓋到自己」的層，沒被蓋到的格子不必跟著留白 */
          const lanes = bars.reduce((n, s) => (ci >= s.c0 && ci <= s.c1 ? Math.max(n, s.lane + 1) : n), 0);
          /* 疊不下的長條併進「+N 更多」，點開視窗一樣看得到（視窗會列出進行中的長條） */
          const over = hidden.filter((s) => ci >= s.c0 && ci <= s.c1).length;
          const budget = Math.max(1, MAX_CHIPS - lanes);
          return {
            ...c,
            lanes,
            events: c.singles.slice(0, budget),
            more: Math.max(0, c.singles.length - budget) + over,
          };
        }),
      });
    }
    return weeks;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [byDay, anchor, holidayMap, holidayOn, todayYmd, monthSpans, scope]);

  /* 週檢視的 7 天（同樣截斷，一天塞幾十筆會把整欄拉到數千 px） */
  const weekDays = useMemo(() => {
    const s = addDays(anchorDate, -anchorDate.getDay());
    return _.range(7).map((i) => {
      const d = addDays(s, i);
      const ymd = ymdKey(d);
      const all = gridEvents(ymd);
      return {
        ymd,
        d: d.getDate(),
        mon: d.getMonth() + 1,
        dow: d.getDay(),
        weekend: d.getDay() === 0 || d.getDay() === 6,
        holiday: dayHoliday(ymd),
        today: ymd === todayYmd,
        events: all.slice(0, MAX_CHIPS_WEEK),
        more: Math.max(0, all.length - MAX_CHIPS_WEEK),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorDate, byDay, holidayMap, holidayOn, todayYmd]);

  /* 議程檢視：攤平成一條由近到遠的事件流，分頁與搜尋都在 AgendaList 內處理 */
  const agendaEvents = useMemo(
    () =>
      Object.keys(byDay)
        .filter((k) => k >= rangeStart && k <= rangeEnd)
        .sort()
        .reduce((acc, ymd) => acc.concat(byDay[ymd]), []),
    [byDay, rangeStart, rangeEnd]
  );

  /* 假日膠囊關掉時裝飾與議程標籤要一起消失，所以傳「已套用開關」的表給子元件 */
  const shownHolidays = useMemo(() => (holidayOn ? holidayMap : {}), [holidayOn, holidayMap]);

  /* 換月／換週後若被點開的那天已不在畫面上，視窗留著只會誤導，直接關掉 */
  useEffect(() => {
    if (selDay && (selDay < rangeStart || selDay > rangeEnd)) setSelDay(null);
  }, [selDay, rangeStart, rangeEnd]);

  /* 切回工項粒度、或換月後這個專案已經不在畫面上時，把專案視窗收掉 */
  useEffect(() => {
    if (!selProject) return;
    if (scope !== "project" || !projectRows.some((r) => r.key === selProject)) setSelProject(null);
  }, [selProject, scope, projectRows]);

  /**
   * 當日／專案詳情視窗的鎖背景捲動 + 關閉後還原焦點（同 ChangePasswordDialog）。
   * 兩個 MUI Dialog 都帶 disableScrollLock / disableRestoreFocus，這兩件事統一由這裡做。
   * ESC 不在這裡收：MUI 的 Dialog 會攔下 ESC（stopPropagation），window 收不到；
   * 「一次 ESC 兩層都關」改在專案 Dialog 的 onClose 處理（見下方 JSX）。
   *
   * 依賴只放布林的 dialogOpen 而不是 selDay：在視窗裡點另一則事件會換日期，
   * 若依賴 selDay，這個 effect 會整個拆掉再裝回去——body 的 overflow 放掉又鎖回去，
   * 背景會閃一下捲軸、捲動位置也可能被瀏覽器拉走。
   *
   * focus({ preventScroll: true }) 是關鍵：還原焦點到原本那個日格時，
   * 不加這個參數瀏覽器會把該元素捲進視野，畫面就從使用者原本看的位置跳掉了。
   */
  const dialogOpen = !!selDay || !!selProject;
  const focusBack = useRef(null);
  useEffect(() => {
    if (!dialogOpen) return undefined;
    focusBack.current = document.activeElement;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
      const el = focusBack.current;
      focusBack.current = null;
      if (el && typeof el.focus === "function") el.focus({ preventScroll: true });
    };
  }, [dialogOpen]);

  /* ---------- 操作 ---------- */

  const go = (dir) =>
    setAnchor((a) => {
      if (view === "month") {
        const d = new Date(a.y, a.m + dir, 1);
        return { y: d.getFullYear(), m: d.getMonth(), d: 1 };
      }
      const step = view === "week" ? 7 : AGENDA_DAYS;
      const d = addDays(new Date(a.y, a.m, a.d), dir * step);
      return { y: d.getFullYear(), m: d.getMonth(), d: d.getDate() };
    });

  const goToday = () => {
    const n = new Date();
    setAnchor({ y: n.getFullYear(), m: n.getMonth(), d: n.getDate() });
  };

  /*
   * 點事件（小標或跨日長條）一律只開當天的詳情視窗，不離開日曆。
   *
   * 原本工項會直接導去專案詳情，但日曆是拿來「掃過一個月看有什麼事」的，
   * 點一下就被丟到另一頁、還得自己按上一頁回來，等於每看一件事就中斷一次。
   * 視窗已經列得出當天所有事件，要看完整內容再從視窗裡進去就好。
   */
  const openEvent = (ev) => {
    /* 專案模式點長條，開的是那個專案的工項清單 —— 這一頁只負責「哪些專案在跑」，
       細節進視窗看。假日沒有 projKey，仍然走當日議程。 */
    if (scope === "project" && ev.projKey) {
      setSelProject(ev.projKey);
      return;
    }
    /* RD 模式點的是「某人某天」，記下人再開當日視窗：
       只顯示一個數字沒有用，要看得到他那天實際做了哪幾件。
       假日沒有人可言，走一般的當日議程。 */
    setSelPerson(scope === "rd" && ev.kind !== "holiday" ? ev.assigneeKey || "" : null);
    setSelDay(ev.ymd);
  };

  const headLabel = useMemo(() => {
    if (view === "month") return `${anchor.y} 年 ${anchor.m + 1} 月`;
    if (view === "week") {
      const s = range.s;
      const e = range.e;
      if (s.getMonth() === e.getMonth()) {
        return `${s.getFullYear()} 年 ${s.getMonth() + 1} 月 ${s.getDate()} – ${e.getDate()} 日`;
      }
      return `${s.getFullYear()} 年 ${s.getMonth() + 1} 月 ${s.getDate()} 日 – ${e.getMonth() + 1} 月 ${e.getDate()} 日`;
    }
    return `${range.s.getFullYear()}/${range.s.getMonth() + 1}/${range.s.getDate()} – ${range.e.getFullYear()}/${range.e.getMonth() + 1}/${range.e.getDate()}`;
  }, [view, anchor, range]);

  /**
   * 視窗要列的當日事件。
   *
   * 除了當天本來就有的起訖／會議／假日，再補上「長條剛好蓋過這一天」的進行中工項——
   * 沒有這一段的話，點跨日長條中間那幾天會顯示「當天沒有事件」，跟畫面上明明橫著一條長條對不起來。
   * 起點與終點那兩天不補：那兩天本來就有「開始」「截止」兩列，補了會重複。
   *
   * 沒有事件時回同一個常數空陣列，而不是每次 render 生一個新的 []，
   * 否則視窗的 useMemo / 分頁每次父層 render 都會白重算一遍。
   */
  const selEvents = useMemo(() => {
    if (!selDay) return EMPTY_EVENTS;
    /*
     * RD 模式點某人時，視窗列的是「他那天的真實工項」，而不是彙總後的那一列。
     * 從 occurrences（工項粒度）撈而不是 byDay（已經被換成彙總列），
     * 只取「工項開始」——與計數口徑一致，也避免同一件跨日工項的起訖各列一次。
     */
    if (scope === "rd" && selPerson !== null) {
      const mine = occurrences.filter(
        (o) => o.ymd === selDay && o.kind === "start" && (o.assigneeKey || "") === selPerson
      );
      return mine.length ? mine : EMPTY_EVENTS;
    }
    const own = byDay[selDay] || EMPTY_EVENTS;
    /* 長條只畫在月檢視。週檢視的欄位與議程清單都沒有中段那幾天（它們走未扣除 absorbed 的 gridEvents），
       在那裡補進來會變成「欄位寫著『—』、點開卻列出好幾筆」，畫面跟視窗自己打架。 */
    if (view !== "month") return own;
    const mid = monthSpans.list
      .filter((s) => isSpanMiddle(s, selDay))
      .map((s) => {
        const row = {
          ...s,
          id: `${s.id}@${selDay}`,
          ymd: selDay,
          time: "",
          spanMid: true,
          nature: SPAN_MID_NATURE,
          title: s.barLabel,
        };
        /* q 是模糊搜尋的比對字串，在 occurrences 階段就算好了，內容寫的是「… 截止」。
           這一列改寫了性質與標題，不重算的話在搜尋框打「進行期間」會說「找不到符合的事件」，
           但那幾列明明正列在畫面上。 */
        return { ...row, q: searchBlob(row) };
      });
    return mid.length ? [...mid, ...own] : own;
    // occurrences / scope / selPerson 是 RD 分支要讀的：漏掉會讓閉包吃到舊值，
    // 點第二個人時視窗還停在第一個人的工項
  }, [selDay, view, byDay, monthSpans, occurrences, scope, selPerson]);

  /* ---------- 樣式片段 ---------- */

  /* 篩選膠囊（事件類型、顯示專案）：開著時外框與淡底是該類型／專案的色，關掉時劃掉並淡化色塊 */
  const filterChip = (on, c) => ({
    variant: "outlined",
    clickable: true,
    "aria-pressed": on,
    icon: (
      <Box
        component="span"
        sx={{ width: 11, height: 11, borderRadius: "var(--radius-xs)", bgcolor: c, flexShrink: 0, ...(on ? {} : { opacity: 0.35 }) }}
      />
    ),
    sx: {
      fontWeight: 700,
      borderColor: on ? c : "divider",
      bgcolor: on ? `${c}1f` : "background.paper",
      color: on ? "text.primary" : "text.secondary",
      textDecoration: on ? "none" : "line-through",
      "& .MuiChip-icon": { ml: 1.25, mr: -0.25 },
    },
  });
  const rowSx = {
    display: "flex",
    alignItems: "center",
    gap: 1,
    flexWrap: "wrap",
    px: 2.5,
    py: 1.5,
    borderBottom: 1,
    borderColor: "divider",
  };
  const rowLabel = { fontWeight: 700, mr: 0.25 };
  /* 月格、週欄共用的格線 */
  const cellLine = { borderRight: 1, borderBottom: 1, borderColor: "divider" };
  /* 圖例的小色塊 */
  const swatch = (extra) => ({ width: 22, height: 13, borderRadius: "var(--radius-xs)", ...extra });
  const legendItem = { display: "inline-flex", alignItems: "center", gap: 0.75 };

  return (
    <Box className="pg">
      {/* ===== 主卡片：工具列 + 檢視 =====
          當日詳情改成彈出視窗後，這張卡吃得回整個版面寬度，
          外層也就不必再是 flex 容器（原本只是為了讓 380px 的面板並排在右邊）。 */}
      <Card sx={{ overflow: "hidden", borderRadius: 0 }}>
        {/* 第一列：月份／區間導覽 + 今天 + 檢視切換 */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            px: 2.5,
            py: 2.25,
            flexWrap: "wrap",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Tooltip title="上一頁">
              <IconButton aria-label="上一頁" onClick={() => go(-1)}>
                <MdChevronLeft aria-hidden="true" />
              </IconButton>
            </Tooltip>
            <Typography variant="h3" component="div">
              {headLabel}
            </Typography>
            <Tooltip title="下一頁">
              <IconButton aria-label="下一頁" onClick={() => go(1)}>
                <MdChevronRight aria-hidden="true" />
              </IconButton>
            </Tooltip>
            <Button variant="outlined" onClick={goToday}>
              今天
            </Button>
            <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              {scope === "project" ? `${projectsInRange} 個專案` : `${rangeCount} 個事件`}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            {/* 檢視粒度放在檢視切換左邊：先決定「看什麼」（專案／工項），再決定「怎麼排」（月／週／議程） */}
            <ToggleButtonGroup
              exclusive
              size="small"
              value={scope}
              onChange={(_e, v) => {
                if (v) setScope(v);
              }}
              aria-label="檢視粒度"
            >
              {SCOPES.map((s) => (
                <ToggleButton key={s.k} value={s.k}>
                  {s.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={view}
              onChange={(_e, v) => {
                if (v) setView(v);
              }}
              aria-label="檢視模式"
            >
              {VIEWS.map((v) => (
                <ToggleButton key={v.k} value={v.k}>
                  {v.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* 第二列：事件類型膠囊 + 指派人。
            專案與 RD 模式整列收起來 ——「工項開始／截止／里程碑」是工項粒度的分類，
            一個專案一條長條、或一人一列彙總時，它們既篩不出東西，
            也只是多兩排讓人分心的按鈕。 */}
        {scope === "task" && (
        <Box sx={rowSx}>
          <Typography component="span" variant="body2" color="text.secondary" sx={rowLabel}>
            事件類型
          </Typography>
          {KINDS.map((k) => {
            return <Chip key={k.k} {...filterChip(kindShown(k.k), k.c)} label={k.label} onClick={() => toggleKind(k.k)} />;
          })}
          <Box sx={{ flex: 1 }} />
          <TextField
            select
            value={assigneeFilter}
            onChange={(e) => setAssigneeFilter(e.target.value)}
            slotProps={{ select: { native: true }, htmlInput: { "aria-label": "指派人" } }}
          >
            <option value={ALL_ASSIGNEE}>全部指派人</option>
            {assigneeOpts.list.map((a) => (
              <option key={a.k} value={a.k}>
                {a.name}
              </option>
            ))}
            {assigneeOpts.hasNone && <option value={NO_ASSIGNEE}>未指派</option>}
          </TextField>
        </Box>
        )}

        {/* 第三列：顯示專案（設計稿原有） */}
        <Box sx={rowSx}>
          <Typography component="span" variant="body2" color="text.secondary" sx={rowLabel}>
            顯示專案
          </Typography>
          {calProjects.length === 0 && (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              尚無專案
            </Typography>
          )}
          {calProjects.map((p) => {
            return <Chip key={p.key} {...filterChip(calShown(p.key), p.c)} label={p.name} onClick={() => toggleProj(p.key)} />;
          })}
          {calHidden.length > 0 && (
            <Button variant="text" size="small" onClick={() => setCalHidden([])}>
              全部顯示
            </Button>
          )}
        </Box>

        {/* 月／週格永遠會畫出日期，光看格子看不出「是篩掉了還是本來就沒事」，補一行說明 */}
        {view !== "agenda" && emptyRange && (
          <Empty
            title={scope === "project" ? "此期間無進行中的專案" : "此期間無符合篩選條件的事件"}
            variant="plain"
            pad="12px 20px"
          />
        )}

        {/* ===== 月檢視 ===== */}
        {/*
          minmax(0,1fr) 不是 1fr：1fr 的最小值是 auto，也就是「內容的最小寬度」，
          所以一個 white-space:nowrap 的長標題會把那一欄整個撐開、七欄變成寬窄不一。
          minmax(0,…) 把最小值壓成 0，七欄才會真的等寬，長標題交給小標自己截斷。

          月格內的字級、內距、高度一律維持 px：跨日長條覆蓋層的 SPAN_TOP / SPAN_ROW
          是拿「格子 padding-top 6px + 日期列 19px」這組數字算的，跟著大字模式縮放就會疊歪。
        */}
        {view === "month" && (
          /* 窄螢幕的補救同週檢視：七欄壓到 360px 寬時一欄只剩 50px，
             扣掉長條自己的 margin 3px 與 padding 6px 只剩 30 幾 px，跨 1–2 天的長條會整條只剩省略號。
             表頭與每一週各給 min-width，容器不夠寬就橫捲，欄寬維持看得到字。 */
          <Box sx={{ overflowX: "auto" }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7,minmax(0,1fr))", minWidth: 700 }}>
              {WEEKDAYS.map((d) => (
                <Box
                  key={d}
                  sx={{
                    p: "10px",
                    textAlign: "center",
                    fontSize: "12px",
                    fontWeight: 800,
                    color: "text.secondary",
                    ...cellLine,
                  }}
                >
                  {d}
                </Box>
              ))}
            </Box>

            {/* 一週一列。原本整月是一張 7 欄大 grid，但跨日長條要橫跨同一週的好幾格，
                得有個「剛好包住那一週」的定位容器才疊得準，所以改成每週各自一列。
                月末不補空格（同設計稿）：最後一列只有幾格，欄數仍是 7，右邊自然留白。 */}
            {monthWeeks.map((w) => (
              <Box
                key={w.key}
                sx={{ position: "relative", display: "grid", gridTemplateColumns: "repeat(7,minmax(0,1fr))", minWidth: 700 }}
              >
                {w.cells.map((c) =>
                  c.blank ? (
                    <Box key={c.key} sx={{ minHeight: "104px", ...cellLine, bgcolor: "action.hover" }} />
                  ) : (
                    <Box
                      key={c.key}
                      role="button"
                      tabIndex={0}
                      title="檢視當日議程"
                      onClick={() => setSelDay(c.ymd)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") setSelDay(c.ymd);
                      }}
                      sx={{
                        minHeight: "104px",
                        p: "6px 7px",
                        cursor: "pointer",
                        ...cellLine,
                        ...(c.weekend || c.holiday ? { bgcolor: "action.hover" } : {}),
                        /* 選取與今天都用主色外框，但選取多一層底色 + 日期填色，兩者才分得出來 */
                        ...(selDay === c.ymd
                          ? { bgcolor: "primary.light", boxShadow: ring(2) }
                          : c.today
                          ? { boxShadow: ring(2) }
                          : {}),
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
                        <Box
                          component="span"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: "19px",
                            height: "19px",
                            p: "0 4px",
                            boxSizing: "border-box",
                            borderRadius: "var(--radius-pill)",
                            fontSize: "12px",
                            fontWeight: c.today || selDay === c.ymd ? 900 : 700,
                            ...(selDay === c.ymd
                              ? { bgcolor: "primary.main", color: "primary.contrastText" }
                              : c.today
                              ? { color: "primary.main" }
                              : c.holiday
                              ? { color: "error.main" }
                              : c.weekend
                              ? { color: "text.disabled" }
                              : { color: "text.primary" }),
                          }}
                        >
                          {c.day}
                        </Box>
                        {c.holiday && (
                          <Box
                            component="span"
                            title={c.holiday}
                            sx={{
                              fontSize: "9.5px",
                              fontWeight: 800,
                              color: "error.main",
                              bgcolor: "error.light",
                              p: "1px 5px",
                              borderRadius: "var(--radius-xs)",
                              maxWidth: "70%",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {c.holiday}
                          </Box>
                        )}
                      </Box>
                      {/* 讓開蓋到這一格的長條。長條是絕對定位的覆蓋層，沒有這塊佔位的話小標會壓在長條底下 */}
                      {c.lanes > 0 && <Box sx={{ height: `${c.lanes * SPAN_ROW}px` }} />}
                      {c.events.map((e) => (
                        <EvChip key={e.id} ev={e} onOpen={openEvent} />
                      ))}
                      {c.more > 0 && (
                        <ButtonBase
                          title="檢視當日全部事件"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelDay(c.ymd);
                          }}
                          sx={{
                            display: "block",
                            width: "100%",
                            textAlign: "left",
                            fontSize: "10px",
                            fontWeight: 800,
                            p: "2px 5px",
                            mt: "3px",
                            color: "primary.main",
                            ...FOCUS_RING,
                          }}
                        >
                          +{c.more} 更多
                        </ButtonBase>
                      )}
                    </Box>
                  )
                )}

                {/* 跨日長條的覆蓋層。跟日格同寬、同樣 7 欄，所以 gridColumn 的第 n 欄一定對得上第 n 天；
                    top 是固定的 SPAN_TOP（格子 padding + 日期列高），跟格子裡的佔位高度同一套常數。
                    覆蓋層不吃點擊，空白處按下去仍然是「點那一天」。 */}
                {w.bars.length > 0 && (
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      right: 0,
                      top: `${SPAN_TOP}px`,
                      display: "grid",
                      gridTemplateColumns: "repeat(7,minmax(0,1fr))",
                      gridAutoRows: `${SPAN_ROW}px`,
                      pointerEvents: "none",
                    }}
                  >
                    {w.bars.map((s) => (
                      <SpanBar key={s.ev.id} ev={s.ev} seg={s} onOpen={openEvent} />
                    ))}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* ===== 週檢視 ===== */}
        {view === "week" && (
          <Box sx={{ overflowX: "auto" }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7,minmax(0,1fr))", minWidth: 700 }}>
              {weekDays.map((d) => (
                <Box
                  key={`h-${d.ymd}`}
                  sx={{
                    p: "9px 10px",
                    textAlign: "center",
                    ...cellLine,
                    ...(selDay === d.ymd
                      ? { bgcolor: "primary.light", boxShadow: ring(3, "bottom") }
                      : d.today
                      ? { bgcolor: "primary.light" }
                      : {}),
                  }}
                >
                  <Box sx={{ fontSize: "12px", fontWeight: 800, color: "text.secondary" }}>週{WEEKDAYS[d.dow]}</Box>
                  <Box
                    sx={{
                      fontSize: "17px",
                      fontWeight: 900,
                      mt: "2px",
                      color: d.today ? "primary.main" : d.holiday ? "error.main" : "text.primary",
                    }}
                  >
                    {d.mon}/{d.d}
                  </Box>
                  {d.holiday && (
                    <Box sx={{ fontSize: "10px", fontWeight: 800, color: "error.main", mt: "2px" }}>{d.holiday}</Box>
                  )}
                </Box>
              ))}
              {weekDays.map((d) => (
                <Box
                  key={`c-${d.ymd}`}
                  role="button"
                  tabIndex={0}
                  title="檢視當日議程"
                  onClick={() => setSelDay(d.ymd)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") setSelDay(d.ymd);
                  }}
                  sx={{
                    minHeight: "320px",
                    p: "7px 8px",
                    cursor: "pointer",
                    ...cellLine,
                    ...(d.weekend || d.holiday ? { bgcolor: "action.hover" } : {}),
                    ...(selDay === d.ymd
                      ? { bgcolor: "primary.light", boxShadow: ring(2) }
                      : d.today
                      ? { boxShadow: ring(2) }
                      : {}),
                  }}
                >
                  {d.events.length === 0 && (
                    <Box sx={{ fontSize: "11.5px", color: "text.disabled", fontWeight: 600, p: "4px 2px" }}>—</Box>
                  )}
                  {d.events.map((e) => (
                    <EvChip key={e.id} ev={e} dense={false} onOpen={openEvent} />
                  ))}
                  {d.more > 0 && (
                    <ButtonBase
                      title="檢視當日全部事件"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelDay(d.ymd);
                      }}
                      sx={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        fontSize: "11px",
                        fontWeight: 800,
                        p: "3px 5px",
                        mt: "4px",
                        color: "primary.main",
                        ...FOCUS_RING,
                      }}
                    >
                      +{d.more} 更多
                    </ButtonBase>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* ===== 議程檢視 =====
            key 帶 rangeStart：換區間等於換一份清單，搜尋字串與頁碼要跟著歸零，
            否則會停在「第 7 頁」卻是另一段期間的資料。 */}
        {view === "agenda" && (
          <AgendaList
            key={`agenda-${rangeStart}`}
            events={agendaEvents}
            holidays={shownHolidays}
            todayYmd={todayYmd}
            onOpen={openEvent}
          />
        )}

        {/* ===== 圖例 ===== */}
        {/* 專案模式的語彙只有四種，沿用工項模式那份會列出一堆這個模式根本畫不出來的東西 */}
        {scope === "project" && (
          <Typography
            variant="body2"
            color="text.secondary"
            component="div"
            sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", px: 2.5, py: 1.375, borderTop: 1, borderColor: "divider", fontWeight: 700 }}
          >
            <Box component="span" sx={legendItem}>
              <Box component="span" sx={swatch({ bgcolor: "primary.main" })} />
              當日有工項開始（顏色代表專案）
            </Box>
            <Box component="span" sx={{ fontWeight: 600 }}>
              數字為當日開始的工項中，已結案與未結案的件數
            </Box>
            <Box component="span" sx={{ fontWeight: 600 }}>
              空白日期表示該專案當日無工項開始
            </Box>
            <Box component="span" sx={legendItem}>
              <Box component="span" sx={swatch({ bgcolor: "rgba(217,45,32,.20)" })} />
              國定假日
            </Box>
            <Box component="span" sx={{ fontWeight: 600 }}>
              點選任一列可檢視該專案的工項
            </Box>
          </Typography>
        )}
        {scope !== "project" && (
        <Typography
          variant="body2"
          color="text.secondary"
          component="div"
          sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", px: 2.5, py: 1.375, borderTop: 1, borderColor: "divider", fontWeight: 700 }}
        >
          {/* 有起訖且跨日的工項在月格已經改畫成一條長條（起訖兩則小標被長條吸收），
              所以前兩項要寫明是「單日」的小標，第三項才是這次新增的主要視覺語彙 */}
          <Box component="span" sx={legendItem}>
            <Box component="span" sx={swatch({ bgcolor: "background.paper", boxShadow: ring(1) })} />
            單日工項開始
          </Box>
          <Box component="span" sx={legendItem}>
            <Box component="span" sx={swatch({ bgcolor: "primary.main" })} />
            單日截止 / 里程碑 / 會議（顏色代表專案）
          </Box>
          <Box component="span" sx={legendItem}>
            <Box component="span" sx={swatch({ width: 34, borderRadius: "var(--radius-xs) 0 0 var(--radius-xs)", bgcolor: "primary.main" })} />
            跨日長條（平切端表示延續至下一週或下個月）
          </Box>
          <Box component="span" sx={legendItem}>
            <Box component="span" sx={swatch({ bgcolor: "primary.main", boxShadow: "inset 3px 0 0 #ffd7d3" })} />
            逾期
          </Box>
          <Box component="span" sx={legendItem}>
            <Box component="span" sx={swatch({ bgcolor: "rgba(217,45,32,.20)" })} />
            國定假日（日期轉紅）
          </Box>
          <Box component="span" sx={legendItem}>
            <Box component="span" sx={swatch({ bgcolor: "transparent", boxShadow: ring(2) })} />
            今天
          </Box>
        </Typography>
        )}
      </Card>

      {/* ===== 當日詳情視窗 =====
          Dialog 只在有 selDay 時掛載；key 帶 selDay 掛在內容上：換一天只重新掛載內容
          （搜尋字串與頁碼跟著歸零），Dialog 本身不關掉重開。
          鎖捲動與還原焦點交給上面的 dialogOpen effect（理由見該處），所以兩個都關掉 MUI 自己的版本。 */}
      {selDay && (
        <Dialog
          open
          onClose={() => setSelDay(null)}
          maxWidth={false}
          disableScrollLock
          disableRestoreFocus
          slotProps={{ paper: { sx: dialogPaper(640) } }}
        >
          <DayDialog
            key={selDay}
            ymd={selDay}
            events={selEvents}
            isToday={selDay === todayYmd}
            onClose={() => setSelDay(null)}
            onOpen={openEvent}
          />
        </Dialog>
      )}

      {/* ===== 專案詳情視窗（專案模式）=====
          key 帶 projKey：換一個專案就重新掛載內容，搜尋字串與頁碼跟著歸零。 */}
      {selProjectRow && (
        <Dialog
          open
          onClose={(_e, reason) => {
            setSelProject(null);
            /* ESC 一次關兩層：MUI 只把 ESC 交給最上層的 Dialog，而且會 stopPropagation，
               window 上的 keydown 收不到；從當日視窗疊開時，這裡順手把下層的當日視窗也關掉 */
            if (reason === "escapeKeyDown") setSelDay(null);
          }}
          maxWidth={false}
          disableScrollLock
          disableRestoreFocus
          slotProps={{ paper: { sx: dialogPaper(720) } }}
        >
          <ProjectDialog
            key={selProject}
            proj={selProjectRow}
            tasks={projectTasks[selProject] || EMPTY_EVENTS}
            onClose={() => setSelProject(null)}
          />
        </Dialog>
      )}
    </Box>
  );
}

const mapStateToProps = (state) => ({
  projectList: _.get(state, "Project.List", []),
  calendarEvents: _.get(state, "Schedule.Calendar", []),
  workItems: _.get(state, "Schedule.WorkItems", []),
  holidayList: _.get(state, "Holiday.List", []),
});

const mapDispatchToProps = (dispatch) => ({
  GET_ProjectList: (payload, callback, loading) =>
    dispatch({ type: "GET_ProjectList", payload, callback, loading }),
  GET_ScheduleCalendar: (payload, callback, loading) =>
    dispatch({ type: "GET_ScheduleCalendar", payload, callback, loading }),
  GET_ScheduleWorkItems: (payload, callback, loading) =>
    dispatch({ type: "GET_ScheduleWorkItems", payload, callback, loading }),
  GET_HolidayList: (payload, callback, loading) =>
    dispatch({ type: "GET_HolidayList", payload, callback, loading }),
});

App.propTypes = {
  projectList: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  calendarEvents: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  workItems: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  holidayList: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  GET_ProjectList: PropTypes.func,
  GET_ScheduleCalendar: PropTypes.func,
  GET_ScheduleWorkItems: PropTypes.func,
  GET_HolidayList: PropTypes.func,
};

App.defaultProps = {
  projectList: [],
  calendarEvents: [],
  workItems: [],
  holidayList: [],
  GET_ProjectList: () => {},
  GET_ScheduleCalendar: () => {},
  GET_ScheduleWorkItems: () => {},
  GET_HolidayList: () => {},
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
